import { useEffect, useState } from "react";
import { useGetClientsQuery } from "../redux/services/clients/clientsApi";
import InputDropdown from "../shared/InputDropdown";
import { AvatarClient } from "./AvatarClient";
import { useDebounceSearch } from "../hooks";
import { NewClientForm } from "../pages/Clients/NewClientForm";
import { Button } from "../shared";

interface Client {
  _id: string;
  name?: string;
  slug?: string;
  avatar?: string;
  [key: string]: any;
}

interface FormData {
  client?: Client | null;
  newClient?: boolean;
}

interface ClientsDropdownProps {
  form: FormData;
  updateForm: (update: { id: string; value: any }) => void;
  errors?: Record<string, any>;
  onClick: (client: Client) => void;
  onChange?: (client: Client) => void;
  value?: Client | null;
  hideFooter?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  size?: "sm" | "md" | "lg";
  id?: string;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export const ClientsDropdown: React.FC<ClientsDropdownProps> = ({
  form,
  updateForm,
  errors = {},
  onClick,
  onChange,
  value,
  hideFooter = false,
  label = "Client",
  placeholder = "Search clients by name",
  required = true,
  size = "md",
  id = "client",
}) => {
  const { search, handleSearch } = useDebounceSearch();

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const { data: clients, isLoading } = useGetClientsQuery({
    page: pageIndex + 1,
    limit: pageSize,
    sortBy: "name",
    sortOrder: "asc",
    search: search.useSearch,
    searchTerm: search.term,
  });

  const selectedClient = value ?? form?.client ?? null;

  function globalFilter(value: string): void {
    setPagination({ pageIndex: 0, pageSize });
    handleSearch(value);
  }

  const [showForm, setShowForm] = useState<boolean>(Boolean(form?.newClient));

  useEffect(() => {
    setShowForm(Boolean(form?.newClient));
  }, [form?.newClient]);

  function handleFooterButtonClick(): void {
    updateForm({ id: "client", value: {} });
    updateForm({ id: "newClient", value: true });
    setShowForm(true);
  }

  function handleCancel(): void {
    updateForm({ id: "client", value: null });
    updateForm({ id: "newClient", value: false });
    setShowForm(false);
  }

  function handleChange(e: { id: string; value: any }): void {
    const clientDraft = form?.client || {};
    updateForm({
      id: "client",
      value: { ...clientDraft, [e.id]: e.value },
    });
  }

  function handleSelect(client: Client): void {
    onClick(client);
    onChange?.(client);
  }

  return (
    <>
      {showForm ? (
        <>
          <NewClientForm
            form={form?.client || {}}
            updateForm={handleChange}
            errors={errors}
          />
          <Button onClick={handleCancel} variant="link">
            Cancel
          </Button>
        </>
      ) : (
        <InputDropdown
          id={id}
          required={required}
          size={size}
          label={label}
          value={selectedClient}
          onChange={(nextValue: string) => globalFilter(nextValue)}
          onSelect={(item) => handleSelect(item as Client)}
          placeholder={
            isLoading ? "Loading clients..." : placeholder
          }
          data={clients?.clients || []}
          footerButtonClick={!hideFooter ? handleFooterButtonClick : undefined}
          footerButton={
            !hideFooter ? "+ New Client" : undefined
          }
          renderItem={({ item, selected, active }) => {
            const client = item as Client;

            return (
              <AvatarClient
                selected={selected}
                highlighted={active}
                name={client.name}
                slug={client.slug}
                avatar={client.avatar}
                disableCancel
              />
            );
          }}
        />
      )}
    </>
  );
};
